import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueue } from '../../context/QueueContext';
import { useSiteConfig, getSiteHistory, loadSiteHistory } from '../../utils/siteConfig';
import IdentitySection from './personalization/IdentitySection';
import BannersSection from './personalization/BannersSection';
import StoreDataSection from './personalization/StoreDataSection';
import HomeContentSection from './personalization/HomeContentSection';
import CategoriesSection from './personalization/CategoriesSection';
import FeaturedSection from './personalization/FeaturedSection';

const SECTIONS = [
  { key: 'identidade', label: 'Identidade' },
  { key: 'banners', label: 'Banners' },
  { key: 'loja', label: 'Dados da Loja' },
  { key: 'home', label: 'Home' },
  { key: 'categorias', label: 'Categorias em Destaque' },
  { key: 'tecidos', label: 'Tecidos em Destaque' },
];

const fmtDate = (iso) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function AdminPersonalization() {
  const { user } = useQueue();
  useSiteConfig();
  const [section, setSection] = useState('identidade');
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    loadSiteHistory();
  }, []);

  if (user?.role !== 'admin') return <Navigate to="/admin/dashboard" replace />;

  const flashMsg = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  };

  const history = getSiteHistory().slice(0, 8);

  return (
    <div className="pb-16" data-testid="personalization-page">
      <h2 className="font-serif text-2xl text-navy">Personalização do Site</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Altere identidade, banners, dados da loja e destaques da Home sem editar código. Área exclusiva do administrador.
      </p>

      <div className="no-scrollbar -mx-4 mt-4 flex gap-2 overflow-x-auto px-4" data-testid="pers-section-tabs">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSection(s.key)}
            aria-pressed={section === s.key}
            data-testid={`pers-tab-${s.key}`}
            className={`shrink-0 rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
              section === s.key ? 'border-navy bg-navy text-white' : 'border-navy/20 text-foreground/70 hover:border-navy'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {section === 'identidade' && <IdentitySection flash={flashMsg} />}
        {section === 'banners' && <BannersSection flash={flashMsg} />}
        {section === 'loja' && <StoreDataSection flash={flashMsg} />}
        {section === 'home' && <HomeContentSection flash={flashMsg} />}
        {section === 'categorias' && <CategoriesSection flash={flashMsg} />}
        {section === 'tecidos' && <FeaturedSection flash={flashMsg} />}
      </div>

      <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid="pers-history">
        <h3 className="font-serif text-lg text-navy">Histórico de alterações</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Auditoria persistente das ações administrativas, gravada no servidor.
        </p>
        <ul className="mt-3 space-y-2">
          {history.map((h, i) => (
            <li key={`${h.at}-${i}`} className="rounded-xl bg-navy-light/40 px-4 py-2.5 text-xs" data-testid="pers-history-entry">
              <span className="font-semibold text-navy">{fmtDate(h.at)}</span>
              <span className="text-muted-foreground"> · {h.userName || 'Admin'} · </span>
              <span>{h.action}</span>
            </li>
          ))}
          {history.length === 0 && (
            <li className="text-sm text-muted-foreground" data-testid="pers-history-empty">Nenhuma alteração registrada ainda.</li>
          )}
        </ul>
      </section>

      {flash && (
        <div className="fixed inset-x-0 bottom-4 z-[75] flex justify-center px-4" data-testid="pers-toast">
          <p className="rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white shadow-lg">{flash}</p>
        </div>
      )}
    </div>
  );
}
